import { DatePipe } from "@angular/common"
import { Component, ElementRef, Input, OnDestroy, OnInit } from "@angular/core"
import { BsModalService } from "ngx-bootstrap/modal"
import { AppState, RoleMgr, ShowRequest } from "../../../../core"
import { Building, ScoutPath, ScoutPoint } from "../../../../core/models"
import { ScoutTaskDoc } from "../../../../core/schema/scout-task-schema"
import { BuildingService, ScoutTaskService } from "../../../../core/services"
import { TableColumn } from "../../../../shared/components/table-view/table-view-component-base"
import { ScoutDataTableBase } from "../scout-data-table/scout-data-table-base"


class BuildingPoint {
  pathName: string
  building : Building
  path : ScoutPath
  point : ScoutPoint
}


@Component({
    selector: 'app-scout-building-points-table',
    templateUrl: 'building-points-table.component.html',
    styleUrls: [
      '../../../../shared/components/table-view/scss/table-view.scss',
      '../scout-data-table/scout-data-table.scss',
      'building-points-table.component.scss'
    ]
  })
  export class ScoutBuildingPointsTableComponent extends ScoutDataTableBase<BuildingPoint> implements OnInit, OnDestroy {
  
    constructor(
        appState: AppState,
        elementRef: ElementRef,
        datePipe: DatePipe,
        roleMgr: RoleMgr,
        bsModalService: BsModalService,
        public scoutTaskService: ScoutTaskService,
        public buildingService: BuildingService,
    ){
      super(appState,elementRef,datePipe,roleMgr,bsModalService)
  
      const initColumnDef = (col: any,i:number) => {
        let cssCategory = 'orange'
        switch(col.name) {
          case 'building.id':
          case 'pathName':
          case 'point.name':
          case 'path.description':
            cssCategory = 'gray'
            break;
          case 'physicalFloor':
          case 'path.floor':
          case 'point.envData.quadrant':
          case 'testPointType':
          case 'point.envData.nearestWindowDistance':
            cssCategory = 'yellow'
            break;
          case 'floorIdAndDescription':
          case 'point.envData.pointDescription':
          case 'point.refData.location1Distance':
          case 'point.refData.location2Distance':
            cssCategory = 'blue'
            break;
          case 'building.construction':
          case 'point.envData.windowCount':
          case 'point.floorToCeiling':
          case 'point.envData.clutterDescription':
          case 'point.envData.hvacDescription':
            cssCategory = 'light-blue'
            break;
          case 'point.exPointId':
          case 'pointId':
            cssCategory = 'green'
            break;
          default:
            if ( col.name?.split('.')[0] == 'building' && col.name != 'building.height') {
              cssCategory = 'blue'
            }
        }
        
        const headerCellCssClass = 'hdr-cell-' + cssCategory
        col.visible = col.visible !== false
        col.headerCellCssClass = [ col.headerCellCssClass, headerCellCssClass ].filter(cssName=>cssName).join(' ')
        col.dataCellCssClass = [ col.dataCellCssClass ].filter(cssName=>cssName).join(' ')
        if ( !col.type ) {
          col.type = 'text'
        }
        if ( !col.template ) {
          col.template = col.type
        }
        return col as TableColumn
      }
      
      this.tableColumns  = [
        { title: 'Building ID', name: 'building.id'},
        { title: 'Morphology', name: 'building.morphology', visible: false},
        { title: 'Market', name: 'building.market', visible: false},
        { title: 'Use Category', name: 'building.useCategory', visible: false},
        { title: 'Building Category', name: 'building.category', visible: false},
        { title: 'Building Characteristics', name: 'building.characteristics', visible: false},
        { title: 'Building Common Name', name: 'building.commonName', visible: false},
        { title: 'Civic Address', name: 'building.civicAddress', visible: false},
        { title: 'Civic Latitude', name: 'building.civicLatitude', template: 'latLngNumber', visible: false, type: 'numeric'},
        { title: 'Civic Longitude', name: 'building.civicLongitude', template: 'latLngNumber', visible: false, type: 'numeric'},
        { title: 'Building Main Contact Person', name: 'building.contactName', visible: false},
        { title: 'Main Contact Number', name: 'building.contactNumber', visible: false},
        { title: 'Main Contact Email', name: 'building.contactEmail', visible: false},
        { title: 'Path ID', name: 'pathName' },
        { title: 'Segment', name: 'point.name' },
        { title: 'Path Description', name: 'path.description', visible: false},
        { title: 'Physical Floor', name: 'physicalFloor', type: 'number'},
        { title: 'Floor Number/ Name', name: 'path.floor'},
        { title: 'Building Quadrant', name: 'point.envData.quadrant'},
        { title: 'Test Point Type', name: 'testPointType'},
        { title: 'Distance From Window', name: 'point.envData.nearestWindowDistance', template: 'nearestWindowDistance', type: 'numeric'},
        { title: 'Type', name: 'point.type', template: 'type', visible: false },
        { title: 'Floor ID and Description', name: 'floorIdAndDescription', dataCellCssClass: 'text-left'},
        { title: 'Test Point ID', name: 'pointId', dataCellCssClass: 'text-left' },
        { title: 'Test Point Location and Description', name: 'point.envData.pointDescription', dataCellCssClass: 'description-cell'},
        { title: 'MD from TP to W-Ref 1 (meters)', name: 'point.refData.location1Distance', type: 'numeric'},
        { title: 'MD from TP to W-Ref 2 (meters)', name: 'point.refData.location2Distance', type: 'numeric'},
        { title: 'Latitude', name: 'point.latitude', template: 'latLngNumber', type: 'numeric'},
        { title: 'Longitude', name: 'point.longitude', template: 'latLngNumber', type: 'numeric'},
        { title: 'Geoid', name: 'point.geoId'},
        { title: 'Altitude (Geoid) Feet', name: 'point.geoIdAltitudeFeet', type: 'numeric'},
        { title: 'Altitude (Geoid) Meters', name: 'point.geoIdAltitudeMeters', type: 'numeric'},
        { title: 'WGS84', name: 'point.wgs84'},
        { title: 'Altitude (Ellipsoid) Feet', name: 'point.altitudeEllipsoidFeet', type: 'numeric'},
        { title: 'Altitude (Ellipsoid) Meters', name: 'point.altitudeEllipsoidMeters', type: 'numeric'},
        { title: 'Building Height', name: 'building.height', type: 'numeric' },
        { title: 'Wall Azimuth', name: 'path.wallAzimuth', dataCellCssClass: 'text-left'},
        { title: 'Construction', name: 'building.construction'},
        { title: 'Windows? And Number of in 25\' Radius', name: 'point.envData.windowCount', type: 'numeric'},
        { title: 'Floor to Ceiling Height of Surveyed Area', name: 'point.envData.floorToCeilingHeight', 
          type: 'numeric'},
        { title: 'Area Clutter Description', name: 'point.envData.clutterDescription', 
        dataCellCssClass: 'description-cell'},
        { title: 'HVAC Environment', name: 'point.envData.hvacDescription', 
        dataCellCssClass: 'description-cell'},
      ].map((col,index)=> initColumnDef(col,index))
  
      this.onScoutPointModalChange = ( pt:ScoutPoint ) => {
        this.selectedRecord = this.records?.find(rec=>rec.point.id==pt?.id) 
      }
    }
  
  
    ngOnInit(): void {
      this.init()
    }
  
  
    ngOnDestroy(): void {
      this.destroy()
    }
  

    onDataCellDblClick( column : TableColumn, record : BuildingPoint ) {
      this.selectedRecord = record
      if ( column.name.startsWith('building') ) {
        this.showTabFn( new ShowRequest('tab','building',record.building.id) )
      } else if ( column.name.startsWith('path') || column.name == 'floorIdAndDescription') {
        this.showTabFn( new ShowRequest('tab','scout path',record.path.id,this._scoutTaskDoc) )
      } else {
        this.showScoutPoint( record.path, record.point )
      }
    }


    async processQueryResults(queryResults: any[]) {
      let records = []
      const buildingId = this._scoutTaskDoc.buildingId

      //this is a hack... the buildingId of scout doc should have change listener and all points should reload on change
      const buildingDoc = await this.buildingService.rxCollection.findOne(this._scoutTaskDoc.buildingId).exec()
      const building : Building = buildingDoc?.toJSON()

      function formatFloorNumber( floor : any ) {      
        if ( floor == null || floor == '' ) {
          return '---'
        }
        let numericFloor : number = parseFloat(floor)
        if ( isNaN(numericFloor) ) {
          return floor
        }
        var j = numericFloor % 10,
            k = numericFloor % 100;
        let suffix = ''
        if (j == 1 && k != 11) {
            suffix = "st";
        } else if (j == 2 && k != 12) {
            suffix = "nd";
        } else if (j == 3 && k != 13) {
            suffix = "rd";
        } else {
            suffix = "th"
        }
        return `${numericFloor}${suffix}`;
      }

      for( let i = 0; i < queryResults?.length; i++ ) {        
        const path = queryResults[i];
        const pathName = path.pathNumber ? buildingId + 'P' + `${path.pathNumber}`.padStart(2,'0') : ''
        let buildingPoints = [] // [this.startPoint] //for plotter without edit mode enabled, just need HA start point info
        this.scoutTaskService.updateScoutPathContext(path,buildingPoints)
        for( let pointIndex = 0; pointIndex < path.scoutPoints?.length; pointIndex++ ) {
          const pt = path.scoutPoints[pointIndex];
          
          if ( pt?.type == 'VIA' ) {
            continue
          }

          //console.log(JSON.stringify(pt))
          const record = {
            pathName,
            building : buildingDoc,
            path : path,
            point : pt
          };

          if ( pt.type == 'HA' && pt.pointId ) {
            record['pointId'] = pt.pointId;
          } else {
            record['pointId'] = pt.exPointId || pt.type
          }

          [ 'id', 'floor', 'modifiedDate', 'description' ].forEach( key => {
            record['path.' + key ] = path[key]
          })

          
          if ( building ) {
            Object.keys(building).forEach( key => {
              record['building.' + key] = building[key]
            })
          } else {
            record['building.id'] = buildingId
          }

          record['floorIdAndDescription'] = formatFloorNumber(path.floor) + ' Floor'
          const metersToFeet = ( metersVal) => {
            if ( isNaN(metersVal) ) {
              metersVal = parseFloat(metersVal)
            }
            return isNaN(metersVal) ? null : metersVal*3.2808
          }
          if ( pt ) {
            if ( pt.envData ) {
              const bInterior = pt.envData.bInterior
              const testPointType = bInterior != null ? ( bInterior ? 'Interior' : 'Exterior' ) : ''
              record['testPointType'] = testPointType
              record['floorIdAndDescription'] += ', ' + pt.name + ' ' + testPointType
              record['point.altitudeEllipsoidFeet'] = metersToFeet(pt.altitudeEllipsoidMeters)
            }
            Object.keys(pt).filter(key=>!['refData','envData'].includes(key)).forEach( key => {
              let cellValue = pt[key]
              record['point.' + key] = cellValue
            });
            ['refData','envData'].forEach( objKey => {
              const obj = pt[objKey]
              if ( obj ) {
                Object.keys(obj).forEach( key => {
                  let cellValue = obj[key]                  
                  record['point.' + objKey + '.' + key] = cellValue
                })
              }
            })
          }
          if ( this.applyLocalFilter( record ) ) {
            records.push( record )
          }
        }
      }
      return records
    }
  

    getName = () => 'building-points'
  
  }